import { AfterViewInit, Component, inject, ChangeDetectionStrategy, signal } from '@angular/core';
import {ProductModel} from '../product-model';
import {Entity} from '../../entity/entity';
import {ConfigurationSaveInformation} from '../../model/configuration-save-information';
import {GuruEntity} from '../../model/guru-entity';
import {IecLeadTime} from '../../model/iec-lead-time';
import {SfrEntry} from '../../model/sfr-entry';
import {TagInformation} from '../../model/tag-information';
import {EntityLineHeader} from '../../entity/entity-line-header';
import {Sku} from '../../model/sku';
import {EntityLine} from '../../entity/entity-line';
import {Observable, Subject} from 'rxjs';
import {UnitRatingResponse} from '../../model/unit-rating-response';
import {ConfigurationService} from '../configuration.service';
import {ModelService} from '../../services/model-service';
import {RatingService} from '../../services/rating.service';
import {UnitConversionService} from '../unit-conversion.service';
import {EntityLineService} from '../../services/entity-line-service';
import { HttpErrorResponse } from '@angular/common/http';
import {StringUtils} from '../../utils/string-utils';
import {SelectionUtils} from '../selection-utils';
import {EntityKeyUtils} from '../../utils/entity-key-utils';
import { SkusAndPricesComponent } from '../common/skus-and-prices/skus-and-prices.component';
import { VerticalFloorACabinetFeaturesComponent } from '../vertical-floor-a-product-model/vertical-floor-a-cabinet-features/vertical-floor-a-cabinet-features.component';
import { DirectDriveSecondaryValvePackageComponent } from '../common/direct-drive-secondary-valve-package/direct-drive-secondary-valve-package.component';
import { DirectDrivePrimaryValvePackageComponent } from '../common/direct-drive-primary-valve-package/direct-drive-primary-valve-package.component';
import { VerticalFloorACoilOptionsComponent } from '../vertical-floor-a-product-model/vertical-floor-a-coil-options/vertical-floor-a-coil-options.component';
import { VerticalFloorAControlsComponent } from '../vertical-floor-a-product-model/vertical-floor-a-controls/vertical-floor-a-controls.component';
import { VerticalFloorAMechanicalConnectionsComponent } from '../vertical-floor-a-product-model/vertical-floor-a-mechanical-connections/vertical-floor-a-mechanical-connections.component';
import { DirectDrivePerformanceResultsComponent } from '../common/direct-drive-performance-results/direct-drive-performance-results.component';
import { DirectDrivePerformanceInputComponent } from '../common/direct-drive-performance-input/direct-drive-performance-input.component';
import { IndoorAirQualityComponent } from '../common/indoor-air-quality/indoor-air-quality.component';
import { SystemInputsComponent } from '../common/system-inputs/system-inputs.component';
import { UnitInformationComponent } from '../common/unit-information/unit-information.component';


@Component({
    selector: 'app-vertical-floor-a-sureflow-product-model',
    templateUrl: './vertical-floor-a-sureflow-product-model.component.html',
    styleUrls: ['./vertical-floor-a-sureflow-product-model.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [UnitInformationComponent, SystemInputsComponent, IndoorAirQualityComponent, DirectDrivePerformanceInputComponent, DirectDrivePerformanceResultsComponent, VerticalFloorAMechanicalConnectionsComponent, VerticalFloorAControlsComponent, VerticalFloorACoilOptionsComponent, DirectDrivePrimaryValvePackageComponent, DirectDriveSecondaryValvePackageComponent, VerticalFloorACabinetFeaturesComponent, SkusAndPricesComponent]
})
export class VerticalFloorASureflowProductModelComponent implements AfterViewInit, ProductModel {
  private readonly configurationService = inject(ConfigurationService);
  private readonly modelService = inject(ModelService);
  private readonly ratingService = inject(RatingService);
  private readonly unitConversionService = inject(UnitConversionService);
  private readonly entityLineService = inject(EntityLineService);


  entity!: Entity;
  readonly entityLine = signal<EntityLine>(undefined!);
  entityLineHeader!: EntityLineHeader;
  readonly taggingDetails = signal<TagInformation[]>(undefined!);
  readonly isBusy = signal(false);
  readonly isLoaded = signal(false);
  readonly errorMessage = signal<string>(undefined!);
  private readonly engineKey = signal<string>(undefined!);
  private modelName = 'VerticalFloorA-Sureflow';
  private imagePrefix = 'assets/configurator/Vertical/';
  readonly unitImagePath = signal<string>(undefined!);
  readonly invalidEntities = signal<string[]>(undefined!);
  readonly selectionEntities = signal<GuruEntity[]>(undefined!);
  readonly unitRatingResponse = signal<UnitRatingResponse>(undefined!);
  readonly messages = signal<string[]>(undefined!);
  readonly skus = signal<Sku[]>(undefined!);
  readonly sfrEntries = signal<SfrEntry[]>(undefined!);
  entityLeadTime!: IecLeadTime;
  readonly configurationLeadTime = signal<IecLeadTime>(undefined!);
  slowestLeadTime!: IecLeadTime;

  ngAfterViewInit(): void {
    this.isLoaded.set(true);
    this.isBusy.set(false);
    this.updateUnitRating();
  }

  initialize(): Observable<string> {
    const subject = new Subject<string>();
    this.isBusy.set(true);
    this.configurationService.isBusy.set(true);
    this.configurationService.loadConfiguration(this.modelName, this.entityLineHeader, this.entity.leadTimeCode).subscribe(response => {
      this.engineKey.set(response.engineKey);
      this.entityLine.set(response.line);
      this.taggingDetails.set(response.taggingDetails);
      this.sfrEntries.set(response.sfrEntries);
      this.modelService.executeHierarchy(this.engineKey(), 'hierReset').subscribe(() => {
        this.modelService.getCharacteristicValue(this.engineKey(), 'USUnit', 'FileName').subscribe(result => {
          this.unitImagePath.set(this.imagePrefix + result);
          this.modelService.getInvalidEntities(this.engineKey()).subscribe(invalidEntities => {
            this.invalidEntities.set(invalidEntities);
            this.modelService.getEntitiesStartingWithName(this.engineKey(), 'US').subscribe(entities => {
              this.selectionEntities.set(entities);
              subject.next('Initialization Complete');
            }, error => this.handleError(error));
          }, error => this.handleError(error));
        }, error => this.handleError(error));
      }, error => this.handleError(error));
    }, error => this.handleError(error));
    return subject.asObservable();
  }

  private handleError(error: HttpErrorResponse): void {
    this.errorMessage.set(StringUtils.getErrorMessage(error));
    this.isBusy.set(false);
    this.configurationService.isBusy.set(false);
  }

  getEngineKey(): string {
    return this.engineKey();
  }

  quantityChanged(): void {
    this.configurationService.changeLine(this.entityLine().lineId);
  }

  updateTaggingDetails(details: TagInformation[]): void {
    this.errorMessage.set(undefined!);
    this.entityLine().quantityExtended = details.length;
    this.entityLine().quantity = this.entityLine().quantityExtended + this.entityLine().quantityCancelled;
    this.taggingDetails.set(details);
    this.configurationService.changeLine(this.entityLine().lineId);
  }

  validateUnit(): void {
    this.errorMessage.set(undefined!);
    this.configurationService.isBusy.set(true);
    this.configurationService.validateUnit(this.engineKey()).subscribe(response => {
      this.invalidEntities.set(response.invalidEntities);
      this.selectionEntities.set(response.userSelections);
      this.configurationService.changeLine(this.entityLine().lineId);
      this.loadMessagesAndSkus();
    }, error => this.handleError(error));
  }

  validateAndRateUnit(): void {
    this.errorMessage.set(undefined!);
    this.configurationService.isBusy.set(true);
    this.configurationService.validateUnit(this.engineKey()).subscribe(response => {
      this.invalidEntities.set(response.invalidEntities);
      this.selectionEntities.set(response.userSelections);
      this.configurationService.changeLine(this.entityLine().lineId);
      this.updateUnitRating();
    }, error => this.handleError(error));
  }

  unitTypeChanged(): void {
    this.errorMessage.set(undefined!);
    this.configurationService.changeUnitType(this.engineKey()).subscribe(response => {
      this.invalidEntities.set(response.invalidEntities);
      this.selectionEntities.set(response.selectionEntities);
      this.unitImagePath.set(this.imagePrefix + response.imageFileName);
      this.configurationService.changeLine(this.entityLine().lineId);
      this.updateUnitRating();
    }, error => this.handleError(error));
  }

  systemTypeChanged(): void {
    this.errorMessage.set(undefined!);
    this.configurationService.isBusy.set(true);
    this.configurationService.changeSystemType(this.engineKey()).subscribe(response => {
      this.invalidEntities.set(response.invalidEntities);
      this.selectionEntities.set(response.selectionEntities);
      this.unitImagePath.set(this.imagePrefix + response.imageFileName);
      this.configurationService.changeLine(this.entityLine().lineId);
      this.updateUnitRating();
    }, error => this.handleError(error));
  }

  private updateUnitRating(): void {
    this.configurationService.isBusy.set(true);
    this.configurationService.getRatingResponse(this.engineKey(), this.selectionEntities())
      .subscribe(response => {
        this.unitRatingResponse.set(response);
        this.loadMessagesAndSkus();
      }, error => {
        const errorResponse = new UnitRatingResponse();
        errorResponse.ErrorMessage = error.error;
        this.unitRatingResponse.set(errorResponse);
        this.configurationService.isBusy.set(false);
      });
  }

  private loadMessagesAndSkus(): void {
    this.configurationService.isBusy.set(true);
    this.configurationService.getMessages(this.engineKey()).subscribe(messages => {
      this.messages.set(undefined!);
      if (messages && messages.length > 0) {
        this.messages.set(messages.filter(message => message && message.trim().length > 0));
      }
      this.modelService.getConfigurationData(this.engineKey(), this.entity.priceBasisDate, this.entity.priceListId).subscribe(data => {
        this.configurationLeadTime.set(this.determineBestLeadTime(data.bestLeadTime));
        this.skus.set(data.skus);
        this.configurationService.isBusy.set(false);
      }, error => this.handleError(error));
    }, error => this.handleError(error));
  }

  private determineBestLeadTime(asConfigured: IecLeadTime): IecLeadTime {
    return this.sfrsAreApplied() ? this.slowestLeadTime : asConfigured;
  }

  private sfrsAreApplied(): boolean {
    return this.sfrEntries() && this.sfrEntries().length > 0 && this.sfrEntries().filter(entry => !entry.ignore).length > 0;
  }

  inputUnitsChanged(): void {
    this.errorMessage.set(undefined!);
    this.configurationService.isBusy.set(true);
    this.unitConversionService.convertMeasurementUnits(this.engineKey(), this.selectionEntities()).subscribe(selections => {
      this.selectionEntities.set(selections);
      this.configurationService.changeLine(this.entityLine().lineId);
      this.updateUnitRating();
    }, error => this.handleError(error));
  }

  getImagePrefix(): string {
    return this.imagePrefix;
  }

  isOem(): boolean {
    return SelectionUtils.isOem(this.entity.prnField1);
  }

  getSaveInformation(): ConfigurationSaveInformation {
    const information = new ConfigurationSaveInformation();
    information.engineKey = this.engineKey();
    information.entityKey = EntityKeyUtils.convertToFullEntityKey(this.entity.id);
    information.lineId = this.entityLine().lineId;
    information.ratingResponse = this.unitRatingResponse();
    information.sfrEntries = this.sfrEntries();
    information.taggingInformation = this.taggingDetails();
    information.customerItemId = this.entityLine().customerItemId;
    information.poAmount = this.entityLine().numericUserField3;
    information.prnField1 = this.entity.prnField1;
    information.initialUnitPrice = SelectionUtils.getConfigurationPrice(this.skus(), this.sfrEntries());
    return information;
  }

  saveUnit(): void {
    this.errorMessage.set(undefined!);
    this.isBusy.set(true);
    this.configurationService.isBusy.set(true);
    this.configurationService.saveConfiguration(this.getSaveInformation()).subscribe(() => {
      this.entityLineService.getFullEntityLine(this.entityLineHeader).subscribe(line => {
        this.entityLine.set(line);
        this.entityLineHeader.configurationComplete = line.configurationComplete;
        this.entityLineHeader.quantity = line.quantityExtended;
        this.entityLineHeader.tags = line.externalNotes;
        this.entityLineHeader.description = line.description;
        this.isBusy.set(false);
        this.configurationService.isBusy.set(false);
      });
    }, error => this.handleError(error));
  }

  applyMassUpdate(entities: GuruEntity[]): void {
    this.configurationService.isBusy.set(true);
    this.configurationService.applyMassUpdate(this.engineKey(), entities).subscribe(() => {
      this.validateAndRateUnit();
      this.configurationService.isBusy.set(false);
    }, error => this.handleError(error));
  }

  sfrFlagChanged(): void {
    this.configurationService.changeLine(this.entityLine().lineId);
  }

}
